// "Auto Estimate Loan" — when a property's address-enrichment response
// includes an existing recorded loan balance (a title/public-records
// lookup, exposed on the enrichment response as `ttbData.loanBalance`),
// Seller and Refinance both offer auto-populating the "Existing 1st" loan
// balance/rate from it instead of requiring the user to type it in by
// hand. The underlying data shape and rules are identical between the two
// calculators (see Seller's useSellerNetEngine.js / SalesPriceSection.js
// for the original implementation), so that part is shared here — each
// calculator still wires its own React state around it, since Seller is
// react-hook-form-driven (getValues/setValue) and Refinance/Buyer are
// formData-state-driven.

// The calculator "mode" values (as passed to AddressSearchPreview /
// fetchCityStateForZip) for which the address-enrichment response should
// attempt to estimate an existing loan balance at all — both represent a
// property with an existing loan to account for (paying it off at sale, or
// refinancing it), unlike Buyer (a fresh purchase, no existing loan) or
// Propnine. Two call sites (src/pages/calculators/utils/apiUtils.js and
// src/pages/calculators/hooks/useAddressSearchPreview.js — different
// address-entry flows, e.g. manual search vs. parcel selection) used to
// each hardcode their own `mode === "seller"` check; this is the one place
// to add a mode if another calculator needs the same estimate.
const LOAN_BALANCE_ESTIMATE_MODES = ["seller", "refinance"];

/** Whether this calculator mode should attempt a loan-balance estimate at all. */
export function shouldEstimateLoanBalance(mode) {
  return LOAN_BALANCE_ESTIMATE_MODES.includes(mode);
}

function parseLoanBalance(ttbData) {
  const raw = ttbData?.loanBalance;
  if (raw == null) return null;
  // Most responses shape this as { balance, interestRate }, but some send
  // a bare number/string for just the balance.
  if (typeof raw === "object") return raw;
  return { balance: raw, interestRate: null };
}

/** Whether ttbData has a usable (> 0) existing loan balance to auto-estimate from. */
export function hasAutoEstimatableLoanBalance(ttbData) {
  const loanBalance = parseLoanBalance(ttbData);
  return Number(loanBalance?.balance) > 0;
}

/** The balance to populate "Existing 1st" with when auto-estimating, as a string. */
export function getAutoEstimatedLoanBalance(ttbData) {
  const loanBalance = parseLoanBalance(ttbData);
  return loanBalance?.balance != null ? String(loanBalance.balance) : "0.00";
}

/** The rate to populate "Existing 1st" with when auto-estimating, as a string. */
export function getAutoEstimatedLoanRate(ttbData) {
  const loanBalance = parseLoanBalance(ttbData);
  return loanBalance?.interestRate != null
    ? String(loanBalance.interestRate)
    : "0.00";
}
